import { ref, getCurrentInstance, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useChoresStore } from '@/stores/chores'
import { useRewardsStore } from '@/stores/rewards'
import { useStreaksStore } from '@/stores/streaks'
import { useLogger } from '@/composables/useLogger'

const { logger } = useLogger()

const connected = ref(false)
let eventSource: EventSource | null = null
let reconnectAttempts = 0
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
let pendingReconnect = false
let stopped = true
const BASE_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000

function getReconnectDelay(): number {
  const exponent = Math.min(
    reconnectAttempts,
    Math.ceil(Math.log2(MAX_RECONNECT_DELAY / BASE_RECONNECT_DELAY)),
  )
  const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** exponent, MAX_RECONNECT_DELAY)
  return delay * (0.5 + Math.random() * 0.5)
}

function clearReconnectTimeout(): void {
  if (reconnectTimeout !== null) {
    clearTimeout(reconnectTimeout)
    reconnectTimeout = null
  }
}

function refreshForEvent(type: string): void {
  const chores = useChoresStore()
  const rewards = useRewardsStore()
  const streaks = useStreaksStore()
  const auth = useAuthStore()
  if (type.startsWith('chore.')) {
    chores.fetchChores()
    streaks.fetchStreaks()
  } else if (type.startsWith('completion.')) {
    chores.fetchChores()
    chores.refetchWeekCompletions()
    streaks.fetchStreaks()
    if (auth.isAdult) chores.fetchPendingReviews()
    rewards.fetchPointBalance()
    rewards.fetchPointHistory()
  } else if (type.startsWith('reward.')) {
    rewards.fetchRewards()
    if (auth.isAdult) rewards.fetchPendingFulfillments()
    rewards.fetchPointBalance()
    rewards.fetchPointHistory()
  } else if (type.startsWith('redemption.')) {
    if (auth.isAdult) rewards.fetchPendingFulfillments()
    else rewards.fetchRewardVault()
    rewards.fetchPointHistory()
  }
}

function refreshAll(): void {
  const chores = useChoresStore()
  const rewards = useRewardsStore()
  const streaks = useStreaksStore()
  const auth = useAuthStore()
  const logRefreshError = (error: unknown): void => {
    logger.warn('SSE: reconnect refresh failed', { error })
  }
  chores.fetchChores().catch(logRefreshError)
  chores.refetchWeekCompletions().catch(logRefreshError)
  if (auth.isAdult) chores.fetchPendingReviews().catch(logRefreshError)
  rewards.fetchRewards().catch(logRefreshError)
  if (auth.isAdult) rewards.fetchPendingFulfillments().catch(logRefreshError)
  else rewards.fetchRewardVault().catch(logRefreshError)
  rewards.fetchPointBalance().catch(logRefreshError)
  rewards.fetchPointHistory().catch(logRefreshError)
  streaks.fetchStreaks().catch(logRefreshError)
}

function scheduleReconnect(): void {
  if (stopped) return
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    pendingReconnect = true
    return
  }
  clearReconnectTimeout()
  pendingReconnect = false
  const delay = getReconnectDelay()
  reconnectAttempts++
  reconnectTimeout = setTimeout(() => {
    reconnectTimeout = null
    if (!stopped) connect(true)
  }, delay)
}

async function connect(wasReconnect = false): Promise<void> {
  stopped = false
  clearReconnectTimeout()
  pendingReconnect = false
  const auth = useAuthStore()
  if (!auth.accountId) return

  eventSource?.close()
  eventSource = null
  const url = `/api/v1/accounts/${auth.accountId}/events`
  const source = new EventSource(url, { withCredentials: true })
  eventSource = source

  source.onopen = () => {
    if (stopped || eventSource !== source) return
    connected.value = true
    reconnectAttempts = 0
    if (wasReconnect) refreshAll()
  }

  const eventTypes = [
    'chore.created',
    'chore.updated',
    'chore.deleted',
    'chore.assigned',
    'completion.created',
    'completion.approved',
    'completion.rejected',
    'completion.reverted',
    'reward.created',
    'reward.updated',
    'reward.deleted',
    'reward.redeemed',
    'redemption.fulfilled',
  ] as const

  for (const type of eventTypes) {
    source.addEventListener(type, function handleSSEEvent() {
      refreshForEvent(type)
    })
  }

  source.onerror = () => {
    if (stopped || eventSource !== source) return
    source.close()
    connected.value = false
    logger.warn('SSE: connection error', {})
    scheduleReconnect()
  }
}

function disconnect(): void {
  stopped = true
  clearReconnectTimeout()
  reconnectAttempts = 0
  pendingReconnect = false
  eventSource?.close()
  eventSource = null
  connected.value = false
}

function handleVisibilityChange(): void {
  if (document.visibilityState !== 'visible' || stopped) return
  if (pendingReconnect || !eventSource || eventSource.readyState !== EventSource.OPEN) {
    pendingReconnect = false
    reconnectAttempts = 0
    connect(true)
  }
}

function handleOnline(): void {
  if (stopped) return
  reconnectAttempts = 0
  pendingReconnect = false
  clearReconnectTimeout()
  connect(true)
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('online', handleOnline)
  window.addEventListener('beforeunload', disconnect)
}

export function useSSE() {
  if (getCurrentInstance()) {
    onUnmounted(disconnect)
  }
  return { connect: () => connect(false), disconnect, connected }
}

export function __resetForTesting(): void {
  disconnect()
}
